import {
  AfterContentInit,
  Component,
  ContentChildren,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  inject,
} from '@angular/core';

import { Observable, Subscription, merge, filter, withLatestFrom, map } from 'rxjs';

import { isPresent, unsubscribe } from '@fcom/core/utils';
import { safeMap } from '@fcom/rx';

import { TabStateService } from './tab-state.service';
import { SelectAction, TabLabelComponent } from './tab-label.component';

const selectNextIndex = (action: SelectAction, previousIndex: number, length: number) => {
  const lastIndex = length - 1;
  switch (action) {
    case SelectAction.PREVIOUS:
      return previousIndex !== 0 ? previousIndex - 1 : lastIndex;
    case SelectAction.NEXT:
      return previousIndex !== lastIndex ? previousIndex + 1 : 0;
    case SelectAction.LAST:
      return lastIndex;
    default:
      return 0;
  }
};

@Component({
  selector: 'fin-tab-header',
  styleUrls: ['tab-header.component.scss'],
  templateUrl: 'tab-header.component.html',
  providers: [TabStateService],
  standalone: false,
})
export class TabHeaderComponent implements OnInit, OnDestroy, AfterContentInit {
  private service = inject(TabStateService);

  @Input()
  tabGroupId: string;
  @Input()
  startWith$: Observable<string>;

  @ContentChildren(TabLabelComponent)
  tabs: QueryList<TabLabelComponent>;
  private subscriptions: Subscription = new Subscription();
  private tabEventSubscriptions: Subscription;

  ngOnInit(): void {
    this.service.init(this.tabGroupId);

    this.subscriptions.add(
      this.startWith$
        .pipe(filter((startWith: string) => isPresent(startWith)))
        .subscribe((startWith: string) => this.service.setSelectedTabId(startWith))
    );
    this.subscriptions.add(
      this.service
        .getSelectedTabId()
        .pipe(
          filter(() => isPresent(this.tabs)),
          safeMap((tabId: string) => this.tabs.find((tab) => tab.tabId === tabId)),
          filter((tab: TabLabelComponent) => isPresent(tab))
        )
        .subscribe((tab: TabLabelComponent) => tab.focus())
    );
  }

  ngAfterContentInit(): void {
    this.subscriptions.add(this.tabs.changes.subscribe(() => this.initTabs()));
    this.initTabs();
  }

  ngOnDestroy(): void {
    this.service.clear();
    this.subscriptions = unsubscribe(this.subscriptions);
    this.tabEventSubscriptions = unsubscribe(this.tabEventSubscriptions);
  }

  private initTabs(): void {
    unsubscribe(this.tabEventSubscriptions);
    this.tabEventSubscriptions = new Subscription();
    this.subscribeToSelections(merge(...this.tabs.map((t) => t.clicked$)));
    this.subscribeToSelectActions(merge(...this.tabs.map((t) => t.actions$)));
  }

  private subscribeToSelections(selections$: Observable<TabLabelComponent>) {
    this.tabEventSubscriptions.add(
      selections$.subscribe((selectedTab: TabLabelComponent) => this.service.setSelectedTabId(selectedTab.tabId))
    );
  }

  private subscribeToSelectActions(actions$: Observable<SelectAction>) {
    const selectedTabIndex$ = this.service
      .getSelectedTabId()
      .pipe(safeMap((tabId) => this.tabs.toArray().findIndex((t) => t.tabId === tabId) || 0));
    this.tabEventSubscriptions.add(
      actions$
        .pipe(
          withLatestFrom(selectedTabIndex$),
          map(([action, previousIndex]) => selectNextIndex(action, previousIndex, this.tabs.length)),
          map((idx: number) => this.tabs.toArray()[idx].tabId)
        )
        .subscribe((tabId: string) => this.service.setSelectedTabId(tabId))
    );
  }
}
